import { Text } from '@mantine/core';
import { IconClick } from '@tabler/icons-react';
import { match } from 'ts-pattern';
import type { AIMessage } from '~/common/vm/protocol';
import type { ActionMethod } from '~/ai_tools/playwright_utils/types';
import { CollapsibleCard } from '../CollapsibleCard';

function getActionDisplayName(actionType: ActionMethod): string {
  return match(actionType)
    .with('click', () => 'Click')
    .with('dblclick', () => 'Double Click')
    .with('fill', () => 'Fill')
    .with('type', () => 'Type')
    .with('pressSequentially', () => 'Type Sequentially')
    .with('press', () => 'Press Key')
    .with('check', () => 'Check')
    .with('uncheck', () => 'Uncheck')
    .with('selectOption', () => 'Select Option')
    .with('hover', () => 'Hover')
    .with('focus', () => 'Focus')
    .with('blur', () => 'Blur')
    .with('clear', () => 'Clear')
    .with('scroll', () => 'Scroll')
    .with('scrollIntoViewIfNeeded', () => 'Scroll Into View')
    .with('tap', () => 'Tap')
    .with('dragTo', () => 'Drag')
    .with('setInputFiles', () => 'Upload Files')
    .with('dispatchEvent', () => 'Dispatch Event')
    .with('goto', () => 'Navigate To')
    .with('goBack', () => 'Go Back')
    .with('goForward', () => 'Go Forward')
    .with('reload', () => 'Reload Page')
    .with('waitForTimeout', () => 'Wait')
    .with('swipe', () => 'Swipe')
    .exhaustive();
}

export function AIActionCard({ message }: { message: Extract<AIMessage, { type: 'action' }> }) {
  return (
    <CollapsibleCard
      icon={IconClick}
      title="Action"
      defaultExpanded={true}
      classNames={{
        icon: 'text-gray-500 dark:text-gray-400',
      }}
    >
      <div>
        {message.actionType || message.target ?
          <div className="flex items-center gap-2">
            {!!message.actionType && (
              <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1 dark:border-gray-700 dark:bg-gray-800">
                <Text
                  size="sm"
                  className="font-medium"
                >
                  {getActionDisplayName(message.actionType)}
                </Text>
              </div>
            )}
            {!!message.actionType && !!message.target && (
              <Text
                size="sm"
                className="opacity-60"
              >
                →
              </Text>
            )}
            {!!message.target && (
              <div className="flex-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 dark:border-gray-700 dark:bg-gray-800">
                <Text
                  size="sm"
                  className="break-all"
                >
                  {message.target}
                </Text>
              </div>
            )}
          </div>
        : <Text
            size="sm"
            className="leading-relaxed"
          >
            {message.content}
          </Text>
        }
      </div>
    </CollapsibleCard>
  );
}
